/* eslint-disable react/forbid-prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import GraphqlService from '../../../service/graphqlService';
import { useProject } from '../../../config/ProjectContext';
import { DEPLOY_TYPE_SERIAL, DEPLOY_TYPE_REGEX, DEPLOY_TYPE_MARKET, DEPLOY_TYPE_GLOBAL } from '../../../utils/constants';



export default function StagedDeploymentConfig({
  isEnabled,
  deploymentType,
  targets,
  regex,
  selectedMarkets,
  selectedExcludedValue,
  excludedValues,
  selectedObjectsToDeploy,
  onConfigChange,
  isBundleDeployment = false, // New prop to indicate bundle deployment
  onLoadingChange, // NEW: Callback to notify parent of loading state changes
}) {
  const { project } = useProject();

  const [targetType, setTargetType] = useState('absolute'); // 'absolute' or 'relative'
  const [absoluteValue, setAbsoluteValue] = useState('');
  const [relativeValue, setRelativeValue] = useState('');
  const [candidatesCount, setCandidatesCount] = useState(null);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [candidatesError, setCandidatesError] = useState('');
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  
  // Track if initial calculation has been done (for auto vs manual calculation)
  const [hasInitialCalculation, setHasInitialCalculation] = useState(false);
  // Track if parameters changed after initial calculation (shows recalculate button)
  const [needsRecalculation, setNeedsRecalculation] = useState(false);
  // Track if recalculation is needed due to an error (for different button message)
  const [isRetryAfterError, setIsRetryAfterError] = useState(false);

  // Refs for debouncing and request cancellation
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  // Track previous parameter values to detect changes
  const prevParamsRef = useRef({ regex, selectedMarkets: selectedMarkets?.join(','), targets: targets?.join(','), excludedValues, selectedExcludedValue });

    // Reset user interaction state when component is disabled or props change
  useEffect(() => {
    if (!isEnabled) {
      setHasUserInteracted(false);
      setAbsoluteValue('');
      setRelativeValue('');
      setCandidatesCount(null);
      setCandidatesError('');
      // Reset calculation tracking state
      setHasInitialCalculation(false);
      setNeedsRecalculation(false);
      setIsRetryAfterError(false);
    }
  }, [isEnabled, deploymentType]);

  // Notify parent of loading state changes for full-screen overlay
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoadingCandidates);
    }
  }, [isLoadingCandidates, onLoadingChange]);

  // NEW: Automatic calculation ONLY on first enable (not on parameter changes)
  useEffect(() => {
    if (!isEnabled) {
      setCandidatesCount(null);
      setCandidatesError('');
      return;
    }

    // Only calculate automatically if we haven't done initial calculation yet
    if (!hasInitialCalculation) {
      // Sync prevParamsRef to current values before initial calculation
      // This prevents false "parameter changed" detection when checkbox is first checked
      prevParamsRef.current = { regex, selectedMarkets: selectedMarkets?.join(','), targets: targets?.join(','), excludedValues, selectedExcludedValue };
      calculateCandidates();
    }
  }, [isEnabled]); // Note: intentionally only depend on isEnabled for auto-calculation

  // Detect parameter changes for showing recalculate button
  useEffect(() => {
    if (!isEnabled || !hasInitialCalculation) return;

    const currentParams = { regex, selectedMarkets: selectedMarkets?.join(','), targets: targets?.join(','), excludedValues, selectedExcludedValue };
    const prevParams = prevParamsRef.current;
    
    // Check if parameters actually changed
    const regexChanged = currentParams.regex !== prevParams.regex;
    const marketsChanged = currentParams.selectedMarkets !== prevParams.selectedMarkets;
    const targetsChanged = currentParams.targets !== prevParams.targets;
    const excludedValuesChanged = currentParams.excludedValues !== prevParams.excludedValues;
    const selectedExcludedValueChanged = currentParams.selectedExcludedValue !== prevParams.selectedExcludedValue;
    
    if (!regexChanged && !marketsChanged && !targetsChanged && !excludedValuesChanged && !selectedExcludedValueChanged) return;
    
    // Update prev params ref
    prevParamsRef.current = currentParams;

    // Show recalculate button immediately
    setNeedsRecalculation(true);
    setIsRetryAfterError(false); // Not an error, just parameter change
  }, [isEnabled, hasInitialCalculation, regex, selectedMarkets, targets, excludedValues, selectedExcludedValue]);

  // Cleanup on component unmount - cancel any in-flight requests and timers
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

    // Check if user has provided input for target values
  const hasUserInput = useCallback(() => {
    return (targetType === 'absolute' && absoluteValue.trim() !== '') || 
           (targetType === 'relative' && relativeValue.trim() !== '');
  }, [targetType, absoluteValue, relativeValue]);

  // Calculate the opposite value automatically
  const getCalculatedPercentage = useCallback(() => {
    if (targetType === 'absolute' && absoluteValue && candidatesCount && candidatesCount > 0) {
      const absValue = parseInt(absoluteValue, 10);
      const percentage = (absValue / candidatesCount) * 100;
      return Math.round(percentage); // Round to whole number
    }
    return null;
  }, [targetType, absoluteValue, candidatesCount]);

  const getCalculatedAbsolute = useCallback(() => {
    if (targetType === 'relative' && relativeValue && candidatesCount && candidatesCount > 0) {
      const relValue = parseFloat(relativeValue);
      const absolute = Math.floor((relValue / 100) * candidatesCount); // Round down
      return absolute;
    }
    return null;
  }, [targetType, relativeValue, candidatesCount]);

  // Memoize the configuration object to prevent unnecessary updates
  const config = useMemo(() => {
    let error = '';
    const hasInput = hasUserInput();

    // Only show validation errors if user has interacted with the form
    if (hasUserInteracted && hasInput) {
      if (targetType === 'absolute') {
        const absValue = parseInt(absoluteValue, 10);
        if (absoluteValue && (isNaN(absValue) || absValue <= 0)) {
          error = 'Please enter a valid positive number';
        } else if (candidatesCount !== null && absValue > candidatesCount) {
          error = `Cannot exceed total candidates (${candidatesCount})`;
        }
      } else {
        const relValue = parseInt(relativeValue, 10);
        if (relativeValue && (isNaN(relValue) || relValue <= 0 || relValue > 100)) {
          error = 'Please enter a valid percentage between 0 and 100';
        }
      }
    }

    return {
      isValid: !error && hasInput && candidatesCount !== null && candidatesCount > 0,
      targetType,
      value: targetType === 'absolute' ? parseInt(absoluteValue, 10) : parseInt(relativeValue, 10),
      candidatesCount,
      error,
      hasInput,
    };
  }, [targetType, absoluteValue, relativeValue, candidatesCount, hasUserInput, hasUserInteracted]);

  // Notify parent of configuration changes
  useEffect(() => {
    onConfigChange({
      isValid: config.isValid,
      targetType: config.targetType,
      value: config.value,
      candidatesCount: config.candidatesCount,
    });
  }, [config, onConfigChange]);

  const calculateCandidates = useCallback(async () => {
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    setIsLoadingCandidates(true);
    setCandidatesError('');
    setCandidatesCount(null);

    // Set timeout for request (60 seconds)
    const TIMEOUT_MS = 60000;
    const timeoutId = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setCandidatesError('Request timeout. Click to retry.');
      setIsLoadingCandidates(false);
      setNeedsRecalculation(true);
      setIsRetryAfterError(true);
    }, TIMEOUT_MS);

    try {
      // Check if it's a bundle or an asset
      const isBundle = selectedObjectsToDeploy?.[0]?.type === 'bundle';
      const releaseID = selectedObjectsToDeploy?.[0]?.releaseID;
      const fileId = selectedObjectsToDeploy?.[0]?.fileID;
      
      // Validate we have either a fileId or releaseID
      if (!fileId && !releaseID) {
        clearTimeout(timeoutId);
        setCandidatesError('No assets or bundles selected for deployment');
        setIsLoadingCandidates(false);
        return;
      }
      
      let candidatesParams = {
        projectId: Number(project.code),
      };
      
      // Add fileId or releaseID depending on what's available
      if (isBundle && releaseID) {
        candidatesParams.releaseID = releaseID;
      } else if (fileId) {
        candidatesParams.fileId = fileId;
      }

      // Build parameters based on deployment type
      switch (deploymentType) {
        case DEPLOY_TYPE_SERIAL: // Device deployment
          // For serial deployments, pass the targets array to getCandidates
          if (targets?.length > 0) {
            candidatesParams.targets = targets;
          }
          break;
        case DEPLOY_TYPE_REGEX: // Regex deployment
          if (regex) {
            candidatesParams.targetRegex = [regex];
          }
          break;
        case DEPLOY_TYPE_MARKET: // Market deployment
          if (selectedMarkets?.length > 0) {
            candidatesParams.targetMarket = selectedMarkets;
          }
          break;
        case DEPLOY_TYPE_GLOBAL: // Global deployment
          // Always send target for global deployment
          candidatesParams.target = '*';
          
          // Handle global deployment exclusions
          if (selectedExcludedValue === 'EXCLUDE-SERIAL' && excludedValues) {
            // Ensure excludedValues is a string before calling split
            const excludedValuesStr = typeof excludedValues === 'string' ? excludedValues : '';
            const excludeArray = excludedValuesStr.split(',').map(item => item.trim()).filter(item => item !== '');
            if (excludeArray.length > 0) {
              candidatesParams.exclude = excludeArray;
            }
          } else if (selectedExcludedValue === 'EXCLUDE-MARKET' && excludedValues) {
            // Ensure excludedValues is a string before calling split
            const excludedValuesStr = typeof excludedValues === 'string' ? excludedValues : '';
            const excludeMarketArray = excludedValuesStr.split(',').map(item => item.trim()).filter(item => item !== '');
            if (excludeMarketArray.length > 0) {
              candidatesParams.excludeMarket = excludeMarketArray;
            }
          }
          break;
        default:
          break;
      }

      console.log('candidatesParams:', candidatesParams);
      const result = await GraphqlService.getCandidates(candidatesParams, signal);
      
      // Clear timeout on success
      clearTimeout(timeoutId);
      
      console.log('getCandidates result:', result);
      setCandidatesCount(result.count);
      // Mark initial calculation as complete and clear recalculation flag
      setHasInitialCalculation(true);
      setNeedsRecalculation(false);
      setIsRetryAfterError(false);
      // Update prev params ref to current values after successful calculation
      prevParamsRef.current = { regex, selectedMarkets: selectedMarkets?.join(','), targets: targets?.join(','), excludedValues, selectedExcludedValue };
    } catch (error) {
      // Clear timeout on error
      clearTimeout(timeoutId);
      
      // Handle abort silently (don't show error to user)
      if (error.name === 'AbortError' || signal.aborted) {
        console.log('getCandidates request cancelled');
        return;
      }
      
      console.error('Error calculating candidates:', error);
      setCandidatesError('Failed to calculate candidates. Click to retry.');
      setNeedsRecalculation(true);
      setIsRetryAfterError(true);
    } finally {
      // Only set loading to false if request wasn't aborted
      if (!signal.aborted) {
        setIsLoadingCandidates(false);
      }
    }
  }, [selectedObjectsToDeploy, project, deploymentType, targets, regex, selectedMarkets, selectedExcludedValue, excludedValues]);

  const handleTargetTypeChange = useCallback((newType) => {
    setTargetType(newType);
    // Clear values when switching types
    setAbsoluteValue('');
    setRelativeValue('');
    setHasUserInteracted(true);
  }, []);

  const handleAbsoluteValueChange = useCallback((event) => {
    const value = event.target.value;
    // Only allow positive integers
    if (value === '' || /^\d+$/.test(value)) {
      setAbsoluteValue(value);
      setHasUserInteracted(true);
    }
  }, []);

  const handleRelativeValueChange = useCallback((event) => {
    const value = event.target.value;
    // Only allow whole numbers (no decimals)
    if (value === '' || /^\d+$/.test(value)) {
      setRelativeValue(value);
      setHasUserInteracted(true);
    }
  }, []);

  const handleAbsoluteValueBlur = useCallback(() => {
    if (targetType === 'absolute' && absoluteValue) {
      const calculatedPercentage = getCalculatedPercentage();
      if (calculatedPercentage !== null) {
        console.log('Calculated percentage:', calculatedPercentage);
        // Auto-populate the relative field with the calculated percentage
        setRelativeValue(calculatedPercentage.toString());
      }
    }
  }, [targetType, absoluteValue, getCalculatedPercentage]);

  const handleRelativeValueBlur = useCallback(() => {
    if (targetType === 'relative' && relativeValue) {
      const calculatedAbsolute = getCalculatedAbsolute();
      if (calculatedAbsolute !== null) {
        console.log('Calculated absolute:', calculatedAbsolute);
        // Auto-populate the absolute field with the calculated value
        setAbsoluteValue(calculatedAbsolute.toString());
      }
    }
  }, [targetType, relativeValue, getCalculatedAbsolute]);

  // NEW: Handler for manual recalculation button
  const handleRecalculate = useCallback(() => {
    calculateCandidates();
  }, [calculateCandidates]);


  if (!isEnabled) {
    return null;
  }

  return (
    <div className="p-6 bg-gray-50 rounded-xl mt-4">
      <div className="space-y-4">

        {/* Show recalculate button when parameters have changed or error occurred after initial calculation */}
        {needsRecalculation && !isLoadingCandidates && (
          <div className={`flex items-center justify-center p-4 border rounded-lg ${isRetryAfterError ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
            <button
              type="button"
              onClick={handleRecalculate}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <ArrowPathIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              {isRetryAfterError ? 'Retry Calculation' : 'Calculate Candidates'}
            </button>
            <p className={`ml-3 text-sm ${isRetryAfterError ? 'text-red-700' : 'text-amber-700'}`}>
              {isRetryAfterError 
                ? 'Calculation failed. Click to retry.' 
                : 'Parameters have changed. Click to recalculate.'}
            </p>
          </div>
        )}

        {(candidatesCount !== null || isLoadingCandidates || candidatesError) && (
          <div className="p-3 bg-white border border-gray-200 rounded-lg mt-4" aria-busy={isLoadingCandidates}>
            {isLoadingCandidates ? (
              <div className="flex items-center gap-2" role="status" aria-live="polite">
                <ArrowPathIcon className="w-4 h-4 animate-spin" aria-hidden="true" />
                <span className="text-sm">Calculating candidates...</span>
              </div>
            ) : candidatesError ? (
              <p className="text-red-500 text-sm" role="alert" aria-live="assertive">{candidatesError}</p>
            ) : candidatesCount !== null ? (
              <div>
        <div className="flex flex-col items-end gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={targetType === 'absolute'}
                onChange={() => handleTargetTypeChange('absolute')}
                disabled={isLoadingCandidates}
                className={`text-blue-600 ${isLoadingCandidates ? 'cursor-not-allowed opacity-50' : ''}`}
              />
              <span className={isLoadingCandidates ? 'text-gray-400' : ''}>Absolute</span>
            </label>
            

              <>
                <input
                  value={absoluteValue}
                  onChange={handleAbsoluteValueChange}
                  onBlur={handleAbsoluteValueBlur}
                  placeholder="0"
                  type="text"
                  inputMode="numeric"
                  disabled={targetType !== 'absolute' || isLoadingCandidates}
                  aria-disabled={targetType !== 'absolute' || isLoadingCandidates}
                  aria-describedby={config.error ? 'staged-deployment-error' : undefined}
                  className={`w-20 px-3 py-2 text-center border rounded-md ${
                    config.error ? 'border-red-500' : 'border-gray-300'
                  } ${targetType !== 'absolute' || isLoadingCandidates ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                <span className="text-gray-500">
                  / {candidatesCount || '0'}
                </span>
              </>
 

            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={targetType === 'relative'}
                onChange={() => handleTargetTypeChange('relative')}
                disabled={isBundleDeployment || isLoadingCandidates}
                className={`text-blue-600 ${isBundleDeployment || isLoadingCandidates ? 'cursor-not-allowed opacity-50' : ''}`}
              />
              <span className={isBundleDeployment || isLoadingCandidates ? 'text-gray-400' : ''}>Relative</span>
              {isBundleDeployment && (
                <span className="text-xs text-gray-400 italic">(Not available for bundles)</span>
              )}
            </label>
            
            
              <>
                <input
                  value={relativeValue}
                  onChange={handleRelativeValueChange}
                  onBlur={handleRelativeValueBlur}
                  placeholder="0"
                  type="text"
                  inputMode="numeric"
                  disabled={targetType !== 'relative' || isBundleDeployment || isLoadingCandidates}
                  aria-disabled={targetType !== 'relative' || isBundleDeployment || isLoadingCandidates}
                  aria-describedby={config.error ? 'staged-deployment-error' : undefined}
                  className={`w-20 px-3 py-2 text-center border rounded-md ${
                    config.error ? 'border-red-500' : 'border-gray-300'
                  } ${targetType !== 'relative' || isBundleDeployment || isLoadingCandidates ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                <span className="text-gray-500">%</span>
              </>
         
          </div>
        </div>
              </div>
            ) : null}
          </div>
        )}



        
        {config.error && (
          <p className="text-red-500 text-sm mt-2" id="staged-deployment-error" role="alert" aria-live="assertive">
            {config.error}
          </p>
        )}

      </div>
    </div>
  );
}

StagedDeploymentConfig.propTypes = {
  isEnabled: PropTypes.bool.isRequired,
  deploymentType: PropTypes.string,
  targets: PropTypes.array,
  regex: PropTypes.string,
  selectedMarkets: PropTypes.array,
  selectedExcludedValue: PropTypes.string,
  excludedValues: PropTypes.string,
  selectedObjectsToDeploy: PropTypes.array,
  onConfigChange: PropTypes.func.isRequired,
  isBundleDeployment: PropTypes.bool,
  onLoadingChange: PropTypes.func, // NEW: Callback when loading state changes
};

StagedDeploymentConfig.defaultProps = {
  deploymentType: '',
  targets: [],
  regex: '',
  selectedMarkets: [],
  selectedExcludedValue: null,
  excludedValues: '',
  selectedObjectsToDeploy: [],
  isBundleDeployment: false,
  onLoadingChange: () => {}, // NEW: Default to no-op
};